/**
 * Questionnaire intake for what discovery can't see — V105-3-4 / ADR-0072 §1 (D-105-6),
 * migration 460.
 *
 * Corporate tools, support access, manual data flows, the log destination, who signs staff
 * in: things no repo scan, connector or egress log proves. A short, code-defined questionnaire
 * asks for them; every answer is ATTESTED (a named person said so) and becomes an inventory
 * item (source `intake`) that is still decided like any other (accept / link / dismiss).
 *
 * Elicitation reuses the intake-elicitation contract of agent A1
 * (agent-intake-elicitation-service.ts): proposals carry the field (here the question), a
 * value, a confidence and the QUOTED SPAN of the person's own text that supports it, and only
 * what the text explicitly names is proposed (A1 never infers a jurisdiction; this never infers
 * a vendor). Unlike A1 it is deterministic (registry names and aliases, no LLM), so it runs in
 * federal workspaces too. A proposal is never saved: the person confirms it by submitting.
 */
import { getProvider, parseProductRef, PROVIDERS } from '../connectors/providers.js';
import { productDisplayName, soleProduct } from '../connectors/product-clues.js';
import { generateRandomId } from '../utils/audit-crypto.js';
import { containsSecretOrEmail } from '../utils/secret-filter.js';
import { assertKnownProduct, InfraInventoryError, markSourcesGone, recordInventoryClue } from './infra-inventory-service.js';
import { suggestInfraFunction } from './infra-repo-scanner.js';
export const INFRA_INTAKE_VERSION = 'infra-intake@1';
export const INFRA_INTAKE_QUESTIONS = Object.freeze([
    { key: 'identity_provider', text: 'Who signs your staff in to work systems?', options: ['okta/*', 'microsoft-entra/*', 'google-workspace/*', 'workos/authkit'], function: 'identity' },
    { key: 'corporate_tools', text: 'Which email, chat and document tools does your team use for work about customers?', options: ['google-workspace/*', 'microsoft-365/*', 'slack/*', 'notion/*', 'confluence/*'], function: 'business_support' },
    { key: 'support_access', text: 'Which tools let staff or vendors reach customer accounts or data (support desks, ticketing, password vaults)?', options: ['jira/*', 'servicenow/*', '1password/*', 'slack/*'], function: 'business_support' },
    { key: 'manual_data_flows', text: 'Where does customer data go by hand (exports, spreadsheets, shared drives, email attachments)?', options: ['google-workspace/*', 'microsoft-365/*', 'notion/*', 'confluence/*', 'slack/*'], function: 'business_support' },
    { key: 'observability_destination', text: 'Where do your logs, traces and alerts go?', options: ['grafana/cloud', 'datadog/*', 'newrelic/*', 'sentry/*', 'splunk/*', 'microsoft-sentinel/*'], function: 'observability' },
    { key: 'email_delivery', text: 'Which service sends your product’s email?', options: ['resend/email', 'sendgrid/*', 'aws/ses', 'twilio/*'], function: 'messaging' },
    { key: 'payments', text: 'Who takes payments or runs billing?', options: ['stripe/billing'], function: 'business_support' },
    { key: 'staff_ai_tools', text: 'Which AI tools do staff use directly, outside the product?', options: ['anthropic/*', 'openai/*', 'google-gemini/*', 'github-copilot/*', 'otter/*'], function: 'business_support' },
]);
const BY_KEY = new Map(INFRA_INTAKE_QUESTIONS.map((q) => [q.key, q]));
export const getIntakeQuestion = (key) => BY_KEY.get(key);
const refKey = (r) => `${r.provider_key}/${r.product_id}`;
function parseKnownRef(raw) {
    const p = parseProductRef(raw);
    if (!p)
        throw new InfraInventoryError('invalid', `${String(raw).slice(0, 80)} is not a known provider or product.`);
    // A provider with one product is that product (`resend/*` → `resend/email`), as in the scanner.
    const r = soleProduct(p.provider_key, p.product_id);
    assertKnownProduct(r);
    return r;
}
let TERMS = null;
function terms() {
    if (TERMS)
        return TERMS;
    const out = [];
    const seen = new Set();
    const add = (text, ref, confidence, caseSensitive) => {
        const t = text.trim();
        if (t.length < 3)
            return;
        const k = `${caseSensitive ? t : t.toLowerCase()}|${refKey(ref)}`;
        if (seen.has(k))
            return;
        seen.add(k);
        out.push({ text: t, ref, confidence, caseSensitive });
    };
    for (const p of PROVIDERS) {
        if (p.key === 'gacpro')
            continue;
        const products = p.products ?? [];
        const providerRef = products.length === 1 ? { provider_key: p.key, product_id: products[0].id } : { provider_key: p.key, product_id: '*' };
        // Names are proper nouns as written ("Slack", not "slack"), so common words don't match.
        add(p.name, providerRef, 0.8, true);
        for (const x of products)
            add(productDisplayName({ provider_key: p.key, product_id: x.id }), { provider_key: p.key, product_id: x.id }, 0.9, true);
        for (const a of p.aliases) {
            if (a.kind === 'domain' && a.value.includes('.'))
                add(a.value, providerRef, 0.85, false);
            if (a.kind === 'oauth_app' && /[A-Z]/i.test(a.value) && a.value.length >= 4)
                add(a.value, providerRef, a.confidence, false);
        }
    }
    // Longest first so "Google Workspace" wins over "Google".
    out.sort((a, b) => b.text.length - a.text.length || (a.text < b.text ? -1 : 1));
    TERMS = out;
    return out;
}
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/**
 * Explicit mentions of registry vendors in a person's own words, each with the quoted span
 * (A1's provenance contract). Deterministic; overlapping matches keep the longest term.
 */
export function elicitIntakeMentions(questionKey, text) {
    const t = (text ?? '').slice(0, 4000);
    if (!t.trim())
        return [];
    const taken = [];
    const out = new Map();
    for (const term of terms()) {
        const re = new RegExp(`(?<![A-Za-z0-9])${escapeRe(term.text)}(?![A-Za-z0-9])`, term.caseSensitive ? 'g' : 'gi');
        for (const m of t.matchAll(re)) {
            const s = m.index ?? 0;
            const e = s + m[0].length;
            if (taken.some(([a, b]) => s < b && e > a))
                continue;
            taken.push([s, e]);
            const key = refKey(term.ref);
            if (out.has(key))
                continue;
            out.set(key, { field: questionKey, value: key, confidence: term.confidence, quoted_span: t.slice(s, e) });
        }
    }
    return [...out.values()].sort((a, b) => (String(a.value) < String(b.value) ? -1 : 1));
}
/** The questionnaire with the current (latest-submission) answers per question. */
export async function getInfraIntake(db, workspaceId) {
    const rows = (await db.query(`SELECT * FROM infra_intake_answers WHERE workspace_id = ? ORDER BY attested_at DESC, answer_id ASC`, [workspaceId])).results;
    const latest = new Map();
    for (const r of rows)
        if (!latest.has(r.question_key))
            latest.set(r.question_key, r.submission_id);
    return {
        version: INFRA_INTAKE_VERSION,
        questions: INFRA_INTAKE_QUESTIONS.map((q) => {
            const answers = rows.filter((r) => r.question_key === q.key && r.submission_id === latest.get(q.key));
            return {
                ...q,
                // Saved refs outside the options (elicited, typed) get their display name too: the form never shows a raw ref.
                option_names: Object.fromEntries([
                    ...q.options.map((o) => { const r = parseProductRef(o); return [o, productDisplayName(r)]; }),
                    ...answers.filter((x) => x.choice === 'product' && x.provider_key && x.product)
                        .map((x) => [`${x.provider_key}/${x.product}`, productDisplayName({ provider_key: x.provider_key, product_id: x.product })]),
                ]),
                answers,
                answered_at: answers[0]?.attested_at ?? null,
            };
        }),
    };
}
/**
 * Record the full answer set for one question (a new submission supersedes the previous one).
 * Every product choice becomes (or refreshes) an inventory item with an `intake` source;
 * a product dropped from the answer loses its intake source.
 */
export async function submitInfraIntakeAnswer(db, input) {
    const q = getIntakeQuestion(input.question_key);
    if (!q)
        throw new InfraInventoryError('invalid', 'Unknown intake question.');
    if (!input.actor_user_id || input.actor_user_id.startsWith('system:'))
        throw new InfraInventoryError('invalid', 'An intake answer needs a person.');
    const refs = [...new Map((input.refs ?? []).map((r) => { const p = parseKnownRef(r); return [refKey(p), p]; })).values()];
    const other = (input.other ?? []).map((o) => String(o).trim()).filter(Boolean);
    if (refs.length > 25 || other.length > 10)
        throw new InfraInventoryError('invalid', 'Too many answers for one question.');
    for (const o of other) {
        if (o.length > 300)
            throw new InfraInventoryError('invalid', 'Each other answer must be at most 300 characters.');
        if (containsSecretOrEmail(o))
            throw new InfraInventoryError('invalid', 'An answer looks like it contains a secret or an e-mail address; name the service instead.');
    }
    if (input.none && (refs.length || other.length))
        throw new InfraInventoryError('invalid', 'Choose “none” or list services, not both.');
    if (!input.none && !refs.length && !other.length)
        throw new InfraInventoryError('invalid', 'Choose at least one service, add one, or answer “none”.');
    const now = input.now ?? new Date();
    const nowIso = now.toISOString();
    const newId = input.newId ?? ((p) => generateRandomId(p));
    const submission = newId('isub');
    const keep = new Set();
    const sourceRef = `intake:${q.key}`;
    for (const r of refs) {
        const span = input.quoted_spans?.[refKey(r)];
        const quoted = typeof span === 'string' && span.trim() ? span.trim().slice(0, 300) : null;
        if (quoted && containsSecretOrEmail(quoted))
            throw new InfraInventoryError('invalid', 'A quoted span looks like it contains a secret or an e-mail address.');
        const itemId = await recordInventoryClue(db, input.workspace_id, {
            provider_key: r.provider_key, product: r.product_id,
            suggested_function: q.options.includes(refKey(r)) ? q.function : suggestInfraFunction(r.provider_key, r.product_id),
            source_kind: 'intake', ref_id: sourceRef, confidence: 0.95,
            detail: { question_key: q.key, submission_id: submission, attested_by: input.actor_user_id, provenance: 'attested', ...(quoted ? { quoted_span: quoted } : {}) },
        }, { now, newId });
        keep.add(itemId);
        await db.execute(`INSERT INTO infra_intake_answers (answer_id, workspace_id, submission_id, question_key, choice, provider_key, product, item_id, quoted_span, attested_by, attested_at)
       VALUES (?, ?, ?, ?, 'product', ?, ?, ?, ?, ?, ?)`, [newId('iia'), input.workspace_id, submission, q.key, r.provider_key, r.product_id, itemId, quoted, input.actor_user_id, nowIso]);
    }
    for (const o of other) {
        await db.execute(`INSERT INTO infra_intake_answers (answer_id, workspace_id, submission_id, question_key, choice, other_text, attested_by, attested_at) VALUES (?, ?, ?, ?, 'other', ?, ?, ?)`, [newId('iia'), input.workspace_id, submission, q.key, o, input.actor_user_id, nowIso]);
    }
    if (input.none) {
        await db.execute(`INSERT INTO infra_intake_answers (answer_id, workspace_id, submission_id, question_key, choice, attested_by, attested_at) VALUES (?, ?, ?, ?, 'none', ?, ?)`, [newId('iia'), input.workspace_id, submission, q.key, input.actor_user_id, nowIso]);
    }
    await markSourcesGone(db, input.workspace_id, 'intake', sourceRef, keep, now);
    return getInfraIntake(db, input.workspace_id);
}
/** Display name of an intake ref (`slack/*` → "Slack"). */
export function intakeRefName(raw) {
    const r = parseProductRef(raw);
    return r ? productDisplayName(r) : getProvider(raw)?.name ?? raw;
}
//# sourceMappingURL=infra-intake-service.js.map